

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Scene } from '@/lib/types';

import { useNarrativeItems } from '@/hooks/use-entities';

interface ScenePickerPanelProps {
  selectedScene: Scene | null;
  onSelectScene: (scene: Scene | null) => void;
  onGenerateScript: () => void;
  isGenerating: boolean;
  hasStoryboard: boolean;
  isTokenDepleted?: boolean;
}

export function ScenePickerPanel({
  selectedScene,
  onSelectScene,
  onGenerateScript,
  isGenerating,
  hasStoryboard,
  isTokenDepleted = false,
}: ScenePickerPanelProps) {
  const { t } = useTranslation();
  const { data: scenes, isLoading } = useNarrativeItems<Scene>('scenes');
  const sceneList = scenes ?? [];

  const [searchTerm, setSearchTerm] = useState('');

  const filteredScenes = useMemo(() => {
    if (!searchTerm) return sceneList;
    const term = searchTerm.toLowerCase();
    return sceneList.filter(
      (scene) =>
        scene.name.toLowerCase().includes(term) ||
        scene.shortDescription?.toLowerCase().includes(term)
    );
  }, [sceneList, searchTerm]);

  return (
    <Card
      sx={{
        width: 360,
        minWidth: 360,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t('storyboard.scene_picker.title')}
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder={t('storyboard.scene_picker.search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ opacity: 0.5 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />
      </CardContent>

      {/* Scene List */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filteredScenes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {sceneList.length === 0 ? t('storyboard.scene_picker.no_scenes') : t('storyboard.scene_picker.no_matching_scenes')}
          </Typography>
        ) : (
          <List dense>
            {filteredScenes.map((scene) => (
              <ListItemButton
                key={scene.id}
                selected={selectedScene?.id === scene.id}
                onClick={() => onSelectScene(scene)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                }}
              >
                <ListItemText
                  primary={scene.name}
                  secondary={!selectedScene || selectedScene.id !== scene.id ? scene.description : undefined}
                  primaryTypographyProps={{ fontWeight: 500, noWrap: true }}
                  secondaryTypographyProps={{
                    noWrap: true,
                    sx: { color: selectedScene?.id === scene.id ? 'rgba(255,255,255,0.7)' : 'text.secondary' },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>

      {/* Selected Scene Summary */}
      {selectedScene && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
            {t('storyboard.scene_picker.selected_scene')}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
            {selectedScene.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
            {selectedScene.description || t('storyboard.scene_picker.no_description')}
          </Typography>

          {/* Character/Location chips */}
          {((selectedScene.characterIds?.length ?? 0) > 0 || (selectedScene.locationIds?.length ?? 0) > 0) && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label={t('storyboard.scene_picker.characters_count', { count: selectedScene.characterIds?.length || 0 })}
                size="small"
                variant="outlined"
              />
              <Chip
                label={t('storyboard.scene_picker.locations_count', { count: selectedScene.locationIds?.length || 0 })}
                size="small"
                variant="outlined"
              />
            </Box>
          )}

          <Tooltip title={isTokenDepleted ? t('storyboard.scene_picker.tokens_exhausted') : ''} placement="top">
            <div style={{ width: '100%' }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
                onClick={onGenerateScript}
                disabled={isGenerating || isTokenDepleted}
                sx={{ borderRadius: 2 }}
              >
                {isGenerating ? t('storyboard.scene_picker.generating') : hasStoryboard ? t('storyboard.scene_picker.regenerate_script') : t('storyboard.scene_picker.generate_script')}
              </Button>
            </div>
          </Tooltip>
        </Box>
      )}
    </Card>
  );
}
